import React, { useState } from 'react';
import { partyBoxes } from '../data/partyBoxes';
import { services } from '../data/services';

const CustomOrder = () => {
  const [selectedBoxes, setSelectedBoxes] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedTiers, setSelectedTiers] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    eventLocation: '',
    guestCount: '',
    additionalNotes: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('boxes');

  // Toggle box selection
  const toggleBox = (boxId) => {
    setSelectedBoxes(prev => {
      if (prev.includes(boxId)) {
        // Remove box and its tier
        const newTiers = { ...selectedTiers };
        delete newTiers[boxId];
        setSelectedTiers(newTiers);
        return prev.filter(id => id !== boxId);
      } else {
        // Add box with default tier (index 0)
        setSelectedTiers(prevTiers => ({
          ...prevTiers,
          [boxId]: 0
        }));
        return [...prev, boxId];
      }
    });
  };

  // Toggle service selection
  const toggleService = (serviceId) => {
    setSelectedServices(prev => {
      if (prev.includes(serviceId)) {
        return prev.filter(id => id !== serviceId);
      } else {
        return [...prev, serviceId];
      }
    });
  };

  // Set tier for a selected box (using index)
  const setBoxTier = (boxId, tierIndex) => {
    setSelectedTiers(prev => ({
      ...prev,
      [boxId]: tierIndex
    }));
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Parse price string to number
  const parsePrice = (priceStr) => {
    if (!priceStr) return 0;
    const match = priceStr.match(/\d+/);
    return match ? parseInt(match[0]) : 0;
  };

  // Calculate estimated total
  const calculateTotal = () => {
    let total = { min: 0, max: 0 };

    // Add box prices
    selectedBoxes.forEach(boxId => {
      const box = partyBoxes.find(b => b.id === boxId);
      const tierIndex = selectedTiers[boxId] ?? 0;
      if (box && box.tiers && box.tiers[tierIndex]) {
        const price = parsePrice(box.tiers[tierIndex].price);
        total.min += price;
        total.max += price;
      }
    });

    // Add service prices
    selectedServices.forEach(serviceId => {
      const service = services.find(s => s.id === serviceId);
      if (service && service.priceRange) {
        total.min += service.priceRange.min;
        total.max += service.priceRange.max;
      }
    });

    return total;
  };

  // Format selection summary for form submission
  const getSelectionSummary = () => {
    let summary = [];

    if (selectedBoxes.length > 0) {
      summary.push('PARTY BOXES:');
      selectedBoxes.forEach(boxId => {
        const box = partyBoxes.find(b => b.id === boxId);
        const tierIndex = selectedTiers[boxId] ?? 0;
        const tierInfo = box?.tiers?.[tierIndex];
        if (box && tierInfo) {
          summary.push(`- ${box.name} (${tierInfo.name}) - ${tierInfo.price}`);
        }
      });
    }

    if (selectedServices.length > 0) {
      if (summary.length > 0) summary.push('');
      summary.push('SERVICES:');
      selectedServices.forEach(serviceId => {
        const service = services.find(s => s.id === serviceId);
        if (service) {
          summary.push(`- ${service.name} - $${service.priceRange.min}-$${service.priceRange.max}`);
        }
      });
    }

    const total = calculateTotal();
    summary.push('');
    summary.push(`ESTIMATED TOTAL: $${total.min}${total.max !== total.min ? `-$${total.max}` : ''}`);

    return summary.join('\n');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedBoxes.length === 0 && selectedServices.length === 0) {
      alert('Please select at least one party box or service.');
      return;
    }

    setLoading(true);

    const formDataToSubmit = new FormData();
    formDataToSubmit.append('form-name', 'custom-order');
    formDataToSubmit.append('name', formData.name);
    formDataToSubmit.append('email', formData.email);
    formDataToSubmit.append('phone', formData.phone);
    formDataToSubmit.append('eventDate', formData.eventDate);
    formDataToSubmit.append('eventLocation', formData.eventLocation);
    formDataToSubmit.append('guestCount', formData.guestCount);
    formDataToSubmit.append('selections', getSelectionSummary());
    formDataToSubmit.append('additionalNotes', formData.additionalNotes);

    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataToSubmit).toString()
      });
      setSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error submitting your order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const total = calculateTotal();
  const hasSelections = selectedBoxes.length > 0 || selectedServices.length > 0;

  if (submitted) {
    return (
      <div className="custom-order-page">
        <section className="order-success">
          <div className="container">
            <div className="success-content">
              <span className="success-icon">🎉</span>
              <h1>Order Request Received!</h1>
              <p>
                Thank you for your custom order request! We've received your selections 
                and will review everything carefully. Expect to hear from us within 
                24-48 hours with confirmation and next steps.
              </p>
              <div className="order-summary-box">
                <h3>Your Selections</h3>
                <pre>{getSelectionSummary()}</pre>
              </div>
              <div className="success-buttons">
                <a href="/" className="btn btn-primary">Back to Home</a>
                <a href="/party-boxes" className="btn btn-secondary">Browse More Boxes</a>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="custom-order-page">
      {/* Hidden form for Netlify detection */}
      <form name="custom-order" data-netlify="true" netlify-honeypot="bot-field" hidden>
        <input type="text" name="name" />
        <input type="email" name="email" />
        <input type="tel" name="phone" />
        <input type="date" name="eventDate" />
        <input type="text" name="eventLocation" />
        <input type="number" name="guestCount" />
        <textarea name="selections"></textarea>
        <textarea name="additionalNotes"></textarea>
      </form>

      {/* Hero Section */}
      <section className="order-hero">
        <div className="container">
          <span className="section-tag">Build Your Dream Event</span>
          <h1>Custom Order</h1>
          <p className="hero-subtitle">
            Mix and match party boxes and services to create your perfect celebration. 
            Select everything you need, fill out your details, and we'll handle the rest.
          </p>
        </div>
      </section>

      {/* Main Order Builder */}
      <section className="order-builder">
        <div className="container">
          <div className="order-layout">
            {/* Selection Panel */}
            <div className="selection-panel">
              {/* Tabs */}
              <div className="selection-tabs">
                <button 
                  className={`tab-btn ${activeTab === 'boxes' ? 'active' : ''}`}
                  onClick={() => setActiveTab('boxes')}
                >
                  Party Boxes
                  {selectedBoxes.length > 0 && (
                    <span className="tab-count">{selectedBoxes.length}</span>
                  )}
                </button>
                <button 
                  className={`tab-btn ${activeTab === 'services' ? 'active' : ''}`}
                  onClick={() => setActiveTab('services')}
                >
                  Services
                  {selectedServices.length > 0 && (
                    <span className="tab-count">{selectedServices.length}</span>
                  )}
                </button>
              </div>

              {/* Party Boxes Tab */}
              {activeTab === 'boxes' && (
                <div className="selection-content">
                  <p className="selection-intro">
                    Select one or more party boxes for your event. Choose a tier for each box.
                  </p>
                  <div className="selection-grid">
                    {partyBoxes.map(box => {
                      const isSelected = selectedBoxes.includes(box.id);
                      const selectedTierIndex = selectedTiers[box.id] ?? 0;
                      
                      return (
                        <div 
                          key={box.id} 
                          className={`selection-card ${isSelected ? 'selected' : ''}`}
                        >
                          <div 
                            className="card-header"
                            onClick={() => toggleBox(box.id)}
                          >
                            <div className="card-checkbox">
                              {isSelected && <span>✓</span>}
                            </div>
                            <div className="card-info">
                              <span className="card-category">{box.category}</span>
                              <h3>{box.name}</h3>
                              <p>{box.tagline}</p>
                            </div>
                          </div>
                          
                          {isSelected && box.tiers && (
                            <div className="tier-selector">
                              <span className="tier-label">Select Tier:</span>
                              <div className="tier-options">
                                {box.tiers.map((tier, index) => (
                                  <button
                                    key={index}
                                    className={`tier-btn ${selectedTierIndex === index ? 'active' : ''}`}
                                    onClick={() => setBoxTier(box.id, index)}
                                  >
                                    <span className="tier-name">{tier.name}</span>
                                    <span className="tier-price">{tier.price}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Services Tab */}
              {activeTab === 'services' && (
                <div className="selection-content">
                  <p className="selection-intro">
                    Add styling services to elevate your event. Services can be combined with party boxes.
                  </p>
                  <div className="selection-grid services-grid">
                    {services.map(service => {
                      const isSelected = selectedServices.includes(service.id);
                      
                      return (
                        <div 
                          key={service.id} 
                          className={`selection-card service-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => toggleService(service.id)}
                        >
                          <div className="card-checkbox">
                            {isSelected && <span>✓</span>}
                          </div>
                          <div className="card-info">
                            <h3>{service.name}</h3>
                            <p>{service.tagline}</p>
                            <div className="service-price">
                              ${service.priceRange.min.toLocaleString()} - ${service.priceRange.max.toLocaleString()}
                            </div>
                            <ul className="service-highlights">
                              {service.includes.slice(0, 3).map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Order Summary & Form */}
            <div className="order-sidebar">
              <div className="order-summary">
                <h2>Your Order</h2>
                
                {!hasSelections ? (
                  <p className="empty-order">
                    Select party boxes or services to build your custom order.
                  </p>
                ) : (
                  <>
                    {selectedBoxes.length > 0 && (
                      <div className="summary-section">
                        <h4>Party Boxes</h4>
                        {selectedBoxes.map(boxId => {
                          const box = partyBoxes.find(b => b.id === boxId);
                          const tierIndex = selectedTiers[boxId] ?? 0;
                          const tierInfo = box?.tiers?.[tierIndex];
                          
                          return box && tierInfo ? (
                            <div key={boxId} className="summary-item">
                              <div className="item-details">
                                <span className="item-name">{box.name}</span>
                                <span className="item-tier">{tierInfo.name}</span>
                              </div>
                              <div className="item-actions">
                                <span className="item-price">{tierInfo.price}</span>
                                <button 
                                  className="remove-btn"
                                  onClick={() => toggleBox(boxId)}
                                  aria-label="Remove item"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          ) : null;
                        })}
                      </div>
                    )}

                    {selectedServices.length > 0 && (
                      <div className="summary-section">
                        <h4>Services</h4>
                        {selectedServices.map(serviceId => {
                          const service = services.find(s => s.id === serviceId);
                          
                          return service ? (
                            <div key={serviceId} className="summary-item">
                              <div className="item-details">
                                <span className="item-name">{service.name}</span>
                              </div>
                              <div className="item-actions">
                                <span className="item-price">
                                  ${service.priceRange.min.toLocaleString()}-${service.priceRange.max.toLocaleString()}
                                </span>
                                <button 
                                  className="remove-btn"
                                  onClick={() => toggleService(serviceId)}
                                  aria-label="Remove item"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          ) : null;
                        })}
                      </div>
                    )}

                    <div className="summary-total">
                      <span>Estimated Total</span>
                      <span className="total-price">
                        ${total.min.toLocaleString()}
                        {total.max !== total.min && `-$${total.max.toLocaleString()}`}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Booking Form */}
              <form onSubmit={handleSubmit} className="booking-form">
                <h3>Complete Your Booking</h3>
                
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="eventDate">Event Date *</label>
                    <input
                      type="date"
                      id="eventDate"
                      name="eventDate"
                      value={formData.eventDate}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="guestCount">Guest Count</label>
                    <input
                      type="number"
                      id="guestCount"
                      name="guestCount"
                      value={formData.guestCount}
                      onChange={handleChange}
                      placeholder="25"
                      min="1"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="eventLocation">Event Location</label>
                  <input
                    type="text"
                    id="eventLocation"
                    name="eventLocation"
                    value={formData.eventLocation}
                    onChange={handleChange}
                    placeholder="City, State or Venue Name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="additionalNotes">Additional Notes</label>
                  <textarea
                    id="additionalNotes"
                    name="additionalNotes"
                    value={formData.additionalNotes}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Any special requests, color preferences, or details we should know..."
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary btn-submit"
                  disabled={loading || !hasSelections}
                >
                  {loading ? 'Submitting...' : 'Submit Order Request'}
                </button>

                <p className="form-note">
                  * This is a booking request. We'll confirm availability and final 
                  pricing within 24-48 hours.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CustomOrder;
